import { Box, List, ListItem, Typography } from "@mui/material";
import { PieValueType } from "@mui/x-charts";
import { PieChart } from "@mui/x-charts/PieChart";
import { MakeOptional } from "@mui/x-charts/internals";

const pieChartColors = [
    { id: 1, color: "#92C5F9" },
    { id: 2, color: "#0066CC" },
    { id: 3, color: "#003366" },
    { id: 4, color: "#AFDC8F" },
    { id: 5, color: "#F8AE54" },
    { id: 6, color: "#FF5733" },
    { id: 7, color: "#E30B5C" },
    { id: 8, color: "#FFC300" },
];

type BasePieChartProps = {
    data: MakeOptional<PieValueType, "id">[];
    height?: number;
    formatter?: (value: number) => string;
};

const BasePieChart = ({ data, height, formatter }: BasePieChartProps) => {
    return (
        <PieChart
            colors={pieChartColors.map(c => c.color)}
            series={[
                {
                    data: data,
                    highlightScope: { fade: "global", highlight: "item" },
                    faded: {
                        innerRadius: 30,
                        additionalRadius: -30,
                        color: "gray",
                    },
                    paddingAngle: 1,
                    cornerRadius: 2,
                    valueFormatter: formatter
                        ? value => formatter(value.value)
                        : value => value.value.toString(),
                },
            ]}
            margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
            height={height}
            slotProps={{
                legend: {
                    hidden: true,
                },
            }}
        />
    );
};

type ChartComponentProps = {
    description: string;
    outerStats: string[];
    data: MakeOptional<PieValueType, "id">[];
    height?: number;
    formatter?: (value: number) => string;
};

export const ChartComponent = ({
    description,
    outerStats,
    data,
    height = 200,
    formatter,
}: ChartComponentProps) => {
    return (
        <Box
            className="chart-container"
            display="flex"
            alignContent={"flex-start"}
            flexDirection={"column"}
            minHeight={"320px"}
        >
            {outerStats.length > 0 && (
                <Box>
                    <List>
                        {outerStats.map((stat, index) => (
                            <ListItem key={index}>
                                <Typography variant="subtitle2">{stat}</Typography>
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}

            <Box className="chart-container" mt={"auto"}>
                <Typography ml={"15px"} mt={"10px"} mb={"5px"} variant="subtitle2">
                    {description}
                </Typography>
                <Box display={"flex"} flexDirection={"row"} justifyContent={"center"} gap={1}>
                    <Box width={"50%"}>
                        <BasePieChart data={data} height={height} formatter={formatter} />
                    </Box>
                    <Box className="legend-container" display={"flex"} flexDirection={"column"}>
                        {data.map((item, index) => (
                            <Box
                                className="legend-item"
                                key={index}
                                display={"flex"}
                                flexDirection={"row"}
                                alignItems={"center"}
                            >
                                <Box
                                    style={{
                                        backgroundColor: pieChartColors[index].color,
                                        width: "25px",
                                        height: "15px",
                                        marginRight: "5px",
                                        borderRadius: "20%",
                                    }}
                                />
                                <Typography variant="subtitle2" mr={1}>
                                    {typeof item.label === "string"
                                        ? (item.value % 1 !== 0
                                              ? item.value.toFixed(2)
                                              : item.value) +
                                          " - " +
                                          item.label
                                        : ""}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

type MultiPieChartComponentProps = {
    description: string;
    chartText1: string;
    chartText2: string;
    data1: MakeOptional<PieValueType, "id">[];
    data2: MakeOptional<PieValueType, "id">[];
    height?: number;
    formatter1?: (value: number) => string;
    formatter2?: (value: number) => string;
};

export const MultiPieChartComponent = ({
    description,
    chartText1,
    chartText2,
    data1,
    data2,
    formatter1,
    formatter2,
}: MultiPieChartComponentProps) => {
    return (
        <Box
            className="chart-container"
            display="flex"
            alignContent={"flex-start"}
            flexDirection={"column"}
        >
            <Box className="chart-container">
                <Typography variant="subtitle1" mb={"10px"}>
                    {description}
                </Typography>
                <Box
                    display="flex"
                    flexDirection={"column"}
                    justifyContent="center"
                    alignItems={"centers"}
                    gap={2}
                >
                    <Box
                        display={"flex"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        flexDirection={"row"}
                    >
                        <BasePieChart data={data1} height={130} formatter={formatter1} />
                        <Typography variant="subtitle1">{chartText1}</Typography>
                    </Box>
                    <Box
                        display={"flex"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        flexDirection={"row"}
                    >
                        <BasePieChart data={data2} height={130} formatter={formatter2} />
                        <Typography variant="subtitle1">{chartText2}</Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ChartComponent;
